"use client";

import { forwardRef, useRef } from "react";
import { cn } from "@/lib/utils";

type ButtonRadialFillProps = React.ComponentProps<"button"> & {
	className?: string;
	classNameFill?: string;
};

let gsapPromise: Promise<typeof import("gsap")["default"]> | null = null;

function loadGsap() {
	if (!gsapPromise) {
		gsapPromise = import("gsap").then((module) => module.default);
	}

	return gsapPromise;
}

function canAnimatePointer() {
	if (typeof window === "undefined") {
		return false;
	}

	return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

export const ButtonRadialFill = forwardRef<
	HTMLButtonElement,
	ButtonRadialFillProps
>(function ButtonRadialFill(
	{
		children,
		className,
		classNameFill,
		disabled,
		onPointerEnter,
		onPointerLeave,
		type = "button",
		...props
	},
	forwardedRef,
) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const fillRef = useRef<HTMLSpanElement>(null);

	const getFillData = (event: React.PointerEvent<HTMLButtonElement>) => {
		const button = buttonRef.current;
		if (!button) return null;

		const rect = button.getBoundingClientRect();

		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;

		const maxX = Math.max(x, rect.width - x);
		const maxY = Math.max(y, rect.height - y);
		const size = Math.sqrt(maxX * maxX + maxY * maxY) * 2;

		return { x, y, size };
	};

	const handleEnter = async (event: React.PointerEvent<HTMLButtonElement>) => {
		const fill = fillRef.current;
		const data = getFillData(event);

		if (disabled || !fill || !data || !canAnimatePointer()) return;

		const gsap = await loadGsap();

		gsap.killTweensOf(fill);

		gsap.set(fill, {
			left: data.x,
			top: data.y,
			width: 0,
			height: 0,
			xPercent: -50,
			yPercent: -50,
		});

		gsap.to(fill, {
			width: data.size,
			height: data.size,
			duration: 0.45,
			ease: "power3.out",
		});
	};

	const handleLeave = async (event: React.PointerEvent<HTMLButtonElement>) => {
		const fill = fillRef.current;
		const data = getFillData(event);

		if (disabled || !fill || !data || !canAnimatePointer()) return;

		const gsap = await loadGsap();

		gsap.killTweensOf(fill);

		gsap.set(fill, {
			left: data.x,
			top: data.y,
			xPercent: -50,
			yPercent: -50,
		});

		gsap.to(fill, {
			width: 0,
			height: 0,
			duration: 0.35,
			ease: "power3.inOut",
		});
	};

	return (
		<button
			ref={(node) => {
				buttonRef.current = node;

				if (typeof forwardedRef === "function") {
					forwardedRef(node);
					return;
				}

				if (forwardedRef) {
					forwardedRef.current = node;
				}
			}}
			onPointerEnter={(event) => {
				handleEnter(event);
				onPointerEnter?.(event);
			}}
			onPointerLeave={(event) => {
				handleLeave(event);
				onPointerLeave?.(event);
			}}
			disabled={disabled}
			className={cn(
				"relative inline-flex items-center justify-center overflow-hidden font-manrope whitespace-nowrap cursor-pointer disabled:cursor-not-allowed",
				className,
			)}
			type={type}
			{...props}
		>
			<span
				ref={fillRef}
				className={cn(
					"pointer-events-none absolute z-0 rounded-full bg-gray-300", // #B34000
					classNameFill,
				)}
			/>

			<span className="relative z-10">{children}</span>
		</button>
	);
});
